import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaQuoteLeft, FaLinkedin, FaUser, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { supabase } from '../config/supabase';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTestimonials, setExpandedTestimonials] = useState(new Set());
  const [isHovered, setIsHovered] = useState(false);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  useEffect(() => {
    fetchApprovedTestimonials();
  }, []);

  const startAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) return;

    scrollIntervalRef.current = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const scrollAmount = 0.5; // Smooth scroll speed
        const cardWidth = 350 + 24; // Card width + gap
        const totalWidth = cardWidth * testimonials.length;

        container.scrollLeft += scrollAmount;

        // When we've scrolled past the first set, reset to beginning seamlessly
        if (container.scrollLeft >= totalWidth) {
          container.scrollLeft = 0;
        }
      }
    }, 16); // 60fps
  }, [testimonials.length]);

  // Auto-scroll functionality
  useEffect(() => {
    if (testimonials.length > 0 && !isHovered) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    return () => stopAutoScroll();
  }, [testimonials.length, isHovered, startAutoScroll]);

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const fetchApprovedTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const toggleExpanded = (testimonialId) => {
    setExpandedTestimonials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testimonialId)) {
        newSet.delete(testimonialId);
      } else {
        newSet.add(testimonialId);
      }
      return newSet;
    });
  };

  // Generate fallback avatar with initials and consistent colors
  const getFallbackAvatar = (fullName) => {
    if (!fullName) return { initials: '?', colors: ['indigo', 'blue'] };
    
    let hash = 0;
    for (let i = 0; i < fullName.length; i++) {
      hash = fullName.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const initials = fullName.trim().split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
    
    const colorPairs = [
      ['from-indigo-400', 'to-indigo-600'],
      ['from-blue-400', 'to-blue-600'],
      ['from-green-400', 'to-green-600'],
      ['from-purple-400', 'to-purple-600'],
      ['from-pink-400', 'to-pink-600'],
      ['from-teal-400', 'to-teal-600'],
      ['from-red-400', 'to-red-600'],
      ['from-orange-400', 'to-orange-600'],
      ['from-emerald-400', 'to-emerald-600'],
      ['from-cyan-400', 'to-cyan-600']
    ];
    
    const colors = colorPairs[Math.abs(hash) % colorPairs.length];
    
    return { initials, colors };
  };

  if (loading) {
    return (
      <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-white" style={{ scrollMarginTop: '100px' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              What People Say
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-2xl mx-auto"
            >
              Feedback from colleagues, clients, and collaborators
            </motion.p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-white" style={{ scrollMarginTop: '120px' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              What People Say
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-2xl mx-auto"
            >
              Be the first to share your experience! 
              <a href="/testimonial" className="text-indigo-600 hover:text-indigo-700 font-medium ml-2 underline">
                Submit your testimonial
              </a>
            </motion.p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-white" style={{ scrollMarginTop: '120px' }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            What People Say
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Feedback from colleagues, clients, and collaborators
          </motion.p>
        </div>

        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto pb-4 testimonials-scroll relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' }
          }}
        >
          <div className="flex gap-6 min-w-max px-4">
            {/* First set of testimonials */}
            {testimonials.map((testimonial, index) => {
              const fallbackAvatar = getFallbackAvatar(testimonial.full_name || testimonial.name);
              const uniqueId = `original-${testimonial.id}`;
              
              const isExpanded = expandedTestimonials.has(uniqueId);
              const shouldTruncate = testimonial.message.length > 120;
              const displayText = isExpanded || !shouldTruncate 
                ? testimonial.message 
                : truncateText(testimonial.message);

              return (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 h-fit border border-gray-100 hover:border-emerald-200 flex-shrink-0"
                  style={{ width: '350px' }}
                >
                {/* Quote Icon */}
                <div className="flex justify-start mb-4">
                  <FaQuoteLeft className="text-2xl text-emerald-500" />
                </div>

                {/* Quote Text */}
                <div className="mb-6">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={isExpanded ? 'expanded' : 'collapsed'}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-gray-700 text-sm leading-relaxed italic"
                    >
                      {displayText}
                    </motion.p>
                  </AnimatePresence>
                  
                  {/* Read More/Less Button */}
                  {shouldTruncate && (
                    <button
                      onClick={() => toggleExpanded(uniqueId)}
                      className="mt-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1 transition-colors"
                    >
                      <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                      {isExpanded ? (
                        <FaChevronUp className="text-xs" />
                      ) : (
                        <FaChevronDown className="text-xs" />
                      )}
                    </button>
                  )}
                </div>

                {/* Author Info */}
                <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                  {/* Avatar */}
                  {testimonial.profile_image ? (
                    <img
                      src={testimonial.profile_image}
                      alt={testimonial.full_name || testimonial.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${fallbackAvatar.colors[0]} ${fallbackAvatar.colors[1]} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                      {fallbackAvatar.initials}
                    </div>
                  )}

                  {/* Name and Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">
                      {testimonial.full_name || testimonial.name}
                    </h4>
                    
                    {testimonial.position && (
                      <p className="text-xs text-gray-600 truncate">
                        {testimonial.position}
                      </p>
                    )}
                    
                    {testimonial.company && (
                      <p className="text-xs text-gray-500 truncate">
                        {testimonial.company}
                      </p>
                    )}

                    {/* LinkedIn Link */}
                    {testimonial.linkedin ? (
                      <a
                        href={testimonial.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700 transition-colors mt-1"
                      >
                        <FaLinkedin className="mr-1" />
                        LinkedIn
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 flex items-center mt-1">
                        <FaUser className="mr-1" />
                        No LinkedIn
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>

              
            );
          })}
            
            {/* Duplicate set for seamless loop */}
            {testimonials.map((testimonial, index) => {
              const fallbackAvatar = getFallbackAvatar(testimonial.full_name || testimonial.name);
              const uniqueId = `duplicate-${testimonial.id}`;
              
              const isExpanded = expandedTestimonials.has(uniqueId);
              const shouldTruncate = testimonial.message.length > 120;
              const displayText = isExpanded || !shouldTruncate 
                ? testimonial.message 
                : truncateText(testimonial.message);

              return (
                <motion.div
                  key={`duplicate-${testimonial.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 h-fit border border-gray-100 hover:border-emerald-200 flex-shrink-0"
                  style={{ width: '350px' }}
                >
                  {/* Quote Icon */}
                  <div className="flex justify-start mb-4">
                    <FaQuoteLeft className="text-2xl text-emerald-500" />
                  </div>

                  {/* Testimonial Text */}
                  <div className="mb-6">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={isExpanded ? 'expanded' : 'collapsed'}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-gray-700 text-sm leading-relaxed italic"
                      >
                        {displayText}
                      </motion.p>
                    </AnimatePresence>
                    
                    {shouldTruncate && (
                      <button
                        onClick={() => toggleExpanded(uniqueId)}
                        className="mt-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center space-x-1 transition-colors"
                      >
                        <span>{isExpanded ? 'Read Less' : 'Read More'}</span>
                        {isExpanded ? (
                          <FaChevronUp className="text-xs" />
                        ) : (
                          <FaChevronDown className="text-xs" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Author Info */}
                  <div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
                    {/* Avatar */}
                    {testimonial.profile_image ? (
                      <img
                        src={testimonial.profile_image}
                        alt={testimonial.full_name || testimonial.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${fallbackAvatar.colors[0]} ${fallbackAvatar.colors[1]} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                      {fallbackAvatar.initials}
                    </div>
                  )}

                  {/* Name and Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">
                      {testimonial.full_name || testimonial.name}
                    </h4>
                    
                    {testimonial.position && (
                      <p className="text-xs text-gray-600 truncate">
                        {testimonial.position}
                      </p>
                    )}
                    
                    {testimonial.company && (
                      <p className="text-xs text-gray-500 truncate">
                        {testimonial.company}
                      </p>
                    )}

                    {/* LinkedIn Link */}
                    {testimonial.linkedin ? (
                      <a
                        href={testimonial.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700 transition-colors mt-1"
                      >
                        <FaLinkedin className="mr-1" />
                        LinkedIn
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 flex items-center mt-1">
                        <FaUser className="mr-1" />
                        No LinkedIn
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-12">
          
          <motion.a
            href="/testimonial"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Share Your Experience
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
